import { Component } from '@theme/component';

{
  const storageKey = 'vowWelcomeOfferV1';
  const browsingKey = 'vowWelcomeStartedV1';
  const pendingKey = 'vowWelcomePendingV1';
  const sevenDays = 7 * 24 * 60 * 60 * 1000;

  const readStorage = (storage, key) => {
    try { return JSON.parse(window[storage].getItem(key) || 'null'); } catch { return null; }
  };
  const writeStorage = (storage, key, value) => {
    try { window[storage].setItem(key, JSON.stringify(value)); } catch {}
  };
  const removeStorage = (storage, key) => {
    try { window[storage].removeItem(key); } catch {}
  };

  /**
   * @typedef {object} WelcomeRefs
   * @property {HTMLDialogElement} dialog - Non-modal newsletter offer.
   * @extends {Component<WelcomeRefs>}
   */
  class VowWelcomePopup extends Component {
    requiredRefs = ['dialog'];
    #cleanup = () => {};
    connectedCallback() {
      super.connectedCallback();
      this.#setup();
    }
    updatedCallback() {
      super.updatedCallback();
      this.#cleanup();
      this.#setup();
    }
    disconnectedCallback() {
      this.#cleanup();
      super.disconnectedCallback();
    }
    #setup() {
    const dialog = this.refs.dialog;
    if (!(dialog instanceof HTMLDialogElement)) return;

    const state = dialog.querySelector('[data-vow-welcome-state]');
    const form = dialog.querySelector('#VowWelcomeForm');
    const serverSuccess = state?.dataset.serverSuccess === 'true';
    const serverErrors = state?.dataset.serverErrors === 'true';
    const pending = readStorage('sessionStorage', pendingKey);
    const recentlySubmitted = pending && Date.now() - pending.at < 20 * 60 * 1000;
    const ownReturn = window.location.hash === '#VowWelcomeForm' || recentlySubmitted;
    const confirmedSuccess = serverSuccess && ownReturn;
    const confirmedErrors = serverErrors && ownReturn;
    const forcePreview = new URLSearchParams(window.location.search).get('vow_welcome_preview') === '1'
      && (window.Shopify?.designMode || window.Shopify?.theme?.role === 'unpublished');

    if (window.Shopify?.designMode && !forcePreview) return;
    if (serverSuccess && !confirmedSuccess) return;

    let previousFocus = null;
    let timer = null;
    let shown = false;
    const controller = new AbortController();
    const listenerOptions = { signal: controller.signal };
    this.#cleanup = () => {
      controller.abort();
      if (timer) window.clearTimeout(timer);
      if (dialog.open) dialog.close();
    };

    const intro = dialog.querySelector('[data-vow-welcome-intro]');
    const formStep = dialog.querySelector('[data-vow-welcome-form-step]');
    const startButton = dialog.querySelector('[data-vow-welcome-start]');
    const showSignup = (moveFocus = false) => {
      if (!(formStep instanceof HTMLElement)) return;
      if (intro instanceof HTMLElement) intro.hidden = true;
      formStep.hidden = false;
      dialog.dataset.step = 'signup';
      startButton?.setAttribute('aria-expanded', 'true');
      if (moveFocus) dialog.querySelector('#VowWelcomeEmail')?.focus({ preventScroll: true });
    };
    if (serverSuccess) dialog.dataset.step = 'success';
    else if (serverErrors) showSignup();
    else dialog.dataset.step = formStep?.hidden === false ? 'signup' : 'intro';
    startButton?.addEventListener('click', () => showSignup(true), listenerOptions);

    const isVisible = (element) => element && element.getClientRects().length > 0
      && getComputedStyle(element).visibility !== 'hidden';
    const pageIsBusy = () => {
      if (document.hidden || document.documentElement.hasAttribute('scroll-lock')) return true;
      if (document.querySelector('dialog[open]:not(#VowWelcomePopup), header-drawer details[open], .menu-drawer-container[open]')) return true;
      if (isVisible(document.querySelector('#shopify-pc__banner'))) return true;
      const active = document.activeElement;
      return active instanceof HTMLElement && !dialog.contains(active)
        && active.matches('input, textarea, select, [contenteditable="true"]');
    };

    const openDialog = () => {
      if (shown || dialog.open) return;
      if (pageIsBusy()) {
        timer = window.setTimeout(openDialog, 500);
        return;
      }
      previousFocus = document.activeElement;
      dialog.show();
      if (previousFocus instanceof HTMLElement) previousFocus.focus({ preventScroll: true });
      // The body cannot receive focus; keep automatic offers from focusing the close button.
      if (dialog.contains(document.activeElement) && document.activeElement instanceof HTMLElement) {
        document.activeElement.blur();
      }
      shown = true;
    };

    const dismiss = () => {
      if (timer) window.clearTimeout(timer);
      if (!confirmedSuccess) {
        const saved = readStorage('localStorage', storageKey);
        writeStorage('localStorage', storageKey, { ...saved, dismissedUntil: Date.now() + sevenDays });
      }
      const restoreFocus = dialog.contains(document.activeElement);
      if (dialog.open) dialog.close();
      if (restoreFocus && previousFocus instanceof HTMLElement && previousFocus.isConnected) previousFocus.focus({ preventScroll: true });
    };

    dialog.querySelectorAll('[data-vow-welcome-close]').forEach((button) => button.addEventListener('click', dismiss, listenerOptions));
    dialog.addEventListener('cancel', (event) => {
      event.preventDefault();
      dismiss();
    }, listenerOptions);
    document.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape' || !dialog.open || !dialog.contains(document.activeElement)) return;
      event.preventDefault();
      dismiss();
    }, listenerOptions);
    dialog.addEventListener('click', (event) => {
      if (event.target !== dialog) return;
      const bounds = dialog.getBoundingClientRect();
      if (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom) dismiss();
    }, listenerOptions);

    form?.addEventListener('submit', () => {
      if (!form.checkValidity()) return;
      writeStorage('sessionStorage', pendingKey, { at: Date.now() });
    }, listenerOptions);

    if (confirmedSuccess) {
      writeStorage('localStorage', storageKey, { subscribed: true });
      removeStorage('sessionStorage', pendingKey);
      timer = window.setTimeout(openDialog, 100);
      return;
    }
    if (confirmedErrors) {
      removeStorage('sessionStorage', pendingKey);
      timer = window.setTimeout(openDialog, 100);
      return;
    }

    const schedule = () => {
      if (timer) window.clearTimeout(timer);
      if (shown || dialog.open) return;
      const saved = readStorage('localStorage', storageKey);
      if (!forcePreview && (dialog.dataset.subscribed === 'true' || saved?.subscribed || saved?.dismissedUntil > Date.now())) return;
      let started = readStorage('sessionStorage', browsingKey);
      if (typeof started !== 'number' || started > Date.now()) {
        started = Date.now();
        writeStorage('sessionStorage', browsingKey, started);
      }
      const delay = forcePreview ? 100 : Math.max(0, Number(dialog.dataset.delay) - (Date.now() - started));
      timer = window.setTimeout(openDialog, delay);
    };
    schedule();
    window.addEventListener('pagehide', () => {
      if (timer) window.clearTimeout(timer);
      timer = null;
    }, listenerOptions);
    window.addEventListener('pageshow', (event) => {
      if (event.persisted) schedule();
    }, listenerOptions);
    }
  }
  if (!customElements.get('vow-welcome-popup')) customElements.define('vow-welcome-popup', VowWelcomePopup);
}
